// src/components/Project/InviteUserDialog.tsx
// Unified "Invite Users to Project" - select existing users and/or enter new email (with name for new users).
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
} from '@mui/material';
import {
  inviteUserToProject,
  InviteUserToProjectRequest,
  ProjectUserApiError,
} from '../../services/projectUserApi';
import { getUserAccessToken } from '../../utils/tokenStorage';
import UserSelector from '../UserSelector/UserSelector';
import { User } from '../../services/userApi';

interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  projectId: number;
}

const USER_TYPES = [
  { value: 'TENANT', label: 'Tenant' },
  { value: 'FRAPORT', label: 'Fraport' },
  { value: 'CLIENT', label: 'Client' },
];

const InviteUserDialog: React.FC<InviteUserDialogProps> = ({
  open,
  onClose,
  onSuccess,
  projectId,
}) => {
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [userType, setUserType] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const hasNewUserEntry = email.trim() && name.trim();
  const hasExistingSelection = selectedUsers.length > 0;
  const canInvite = hasNewUserEntry || hasExistingSelection;

  const validateNewUser = (): boolean => {
    if (!email.trim() && !name.trim()) return true;
    const errors: Record<string, string> = {};
    if (!email.trim()) errors.email = 'Email is required for new user';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errors.email = 'Invalid email format';
    if (!name.trim()) errors.name = 'Name is required for new user';
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!canInvite) return;
    if (hasNewUserEntry && !validateNewUser()) return;

    setError(null);
    setIsSubmitting(true);

    const userAccessToken = getUserAccessToken();
    const invitePayload = (data: Partial<InviteUserToProjectRequest>) => ({
      ...data,
      userAccessToken: userAccessToken || undefined,
    });

    try {
      let invited = 0;
      const errors: string[] = [];

      for (const user of selectedUsers) {
        const userEmail = (user as User & { email?: string }).email ?? (user as User & { upn?: string }).upn ?? '';
        if (!userEmail) continue;
        try {
          await inviteUserToProject(projectId, { ...invitePayload({}), email: userEmail });
          invited += 1;
        } catch (err) {
          const apiErr = err as ProjectUserApiError;
          errors.push(apiErr.message || `Failed to invite ${userEmail}`);
        }
      }

      if (hasNewUserEntry) {
        try {
          await inviteUserToProject(projectId, {
            ...invitePayload({}),
            email: email.trim(),
            name: name.trim(),
            company: company.trim() || undefined,
            userType: userType || undefined,
          });
          invited += 1;
        } catch (err) {
          const apiErr = err as ProjectUserApiError;
          errors.push(apiErr.message || 'Failed to invite new user');
        }
      }

      if (errors.length > 0) {
        setError(errors.join('. '));
        if (invited > 0) {
          onSuccess();
          setSelectedUsers([]);
          setEmail('');
          setName('');
          setCompany('');
          setUserType('');
        }
      } else {
        onSuccess();
        handleClose();
      }
    } catch (err) {
      const apiError = err as ProjectUserApiError;
      setError(apiError.message || 'Failed to invite users');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedUsers([]);
      setEmail('');
      setName('');
      setCompany('');
      setUserType('');
      setError(null);
      setValidationErrors({});
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite Users to Project</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Select existing users
            </Typography>
            <UserSelector
              value={selectedUsers}
              onChange={setSelectedUsers}
              placeholder="Search users to invite..."
              multiple
              excludeProjectId={projectId}
            />
          </Box>

          <Divider sx={{ my: 1 }} />

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Or invite by email (new user)
          </Typography>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setValidationErrors({ ...validationErrors, email: '' });
            }}
            fullWidth
            disabled={isSubmitting}
            error={!!validationErrors.email}
            helperText={validationErrors.email || 'Required for new user'}
          />
          <TextField
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setValidationErrors({ ...validationErrors, name: '' });
            }}
            fullWidth
            disabled={isSubmitting}
            error={!!validationErrors.name}
            helperText={validationErrors.name || 'Required for new user'}
          />
          <TextField
            label="Company"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            fullWidth
            disabled={isSubmitting}
            helperText="Optional"
          />
          <FormControl fullWidth disabled={isSubmitting}>
            <InputLabel id="user-type-select-label" shrink>
              User Type
            </InputLabel>
            <Select
              labelId="user-type-select-label"
              value={userType}
              label="User Type"
              onChange={(e) => setUserType(e.target.value)}
              displayEmpty
              renderValue={(selected: string) => {
                if (!selected) return <span style={{ color: '#999', fontStyle: 'italic' }}>Optional</span>;
                return USER_TYPES.find((t) => t.value === selected)?.label || selected;
              }}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {USER_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || !canInvite}
        >
          {isSubmitting ? 'Inviting...' : 'Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InviteUserDialog;
